import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/store/api';
import { getCaseNotesAppendDelta } from '@/utils/caseNotes';
import {
  DEFAULT_CASE_REPORTING_METHOD,
  REPORTING_METHOD_OPTIONS,
  isCaseClosed,
  buildResolutionTypeOptions,
} from '@/components/contact-center/caseFormConstants';
import type {
  CaseFormFields,
  CaseFieldType,
} from '@/components/contact-center/CaseFormDialog';
import {
  CloseCaseLoop,
  type CaseInformationResponse,
  type UpdateCaseRequest,
} from '@/types/case';

function toUniqueNamedOptions(
  values: Array<{ id: string; name: string }>
): Array<{ value: string; label: string }> {
  const seen = new Set<string>();

  return values.reduce<Array<{ value: string; label: string }>>(
    (options, value) => {
      if (seen.has(value.id)) return options;
      seen.add(value.id);
      options.push({ value: value.id, label: value.name });
      return options;
    },
    []
  );
}

function resolveOptionValue(
  options: Array<{ value: string; label: string }>,
  value: string | undefined
): string {
  if (!value) return '';

  const matchedOption = options.find(
    option => option.value === value || option.label === value
  );

  return matchedOption?.value ?? value;
}

export function createEmptyCaseFormFields(defaultStatus = ''): CaseFormFields {
  return {
    subject: '',
    description: '',
    status: defaultStatus,
    category: '',
    categoryCode: '',
    categoryName: '',
    categoryPriority: '',
    caseType: '',
    caseTypeId: null,
    subCaseType: '',
    origination: '',
    closeCaseLoop: CloseCaseLoop.PHONE,
    resolution: '',
    phoneNumber: '',
    email: '',
    reportingMethod: DEFAULT_CASE_REPORTING_METHOD,
    notes: '',
    employeeId: null,
    reportedByEmployeeId: null,
    assignedTo: null,
    resolutionType: '',
    reportedByName: '',
    clientContactId: '',
  };
}

type EditableScalarFieldType = Exclude<CaseFieldType, 'category'>;

const EDITABLE_FIELD_TYPES: Array<EditableScalarFieldType | 'category'> = [
  'status',
  'subject',
  'description',
  'notes',
  'category',
  'categoryPriority',
  'caseType',
  'subCaseType',
  'origination',
  'resolution',
  'resolutionType',
  'phoneNumber',
  'email',
  'employeeId',
  'reportedByEmployeeId',
  'assignedTo',
  'clientContactId',
  'closeCaseLoop',
  'reportedByName',
  'reportingMethod',
];

function isFieldDirty(
  fieldType: CaseFieldType,
  fields: CaseFormFields,
  initial: CaseFormFields
): boolean {
  if (fieldType === 'status') {
    return fields.status !== '' && fields.status !== initial.status;
  }
  if (fieldType === 'category') {
    return (
      fields.categoryCode !== initial.categoryCode ||
      fields.categoryName !== initial.categoryName
    );
  }
  if (fieldType === 'caseType') {
    return (
      fields.caseTypeId !== initial.caseTypeId ||
      fields.caseType !== initial.caseType
    );
  }
  return fields[fieldType] !== initial[fieldType];
}

export interface UseCaseFormEditorOptions {
  caseInfo: CaseInformationResponse | null | undefined;
  contactId: string | null;
  /** When true, form state is synced from `caseInfo`. */
  syncWhen: boolean;
  onAfterSave?: (caseId: number) => void;
  /** Workspace ID for fetching employees dropdown. */
  workspaceId?: number | null;
}

export function useCaseFormEditor({
  caseInfo,
  contactId,
  syncWhen,
  onAfterSave,
  workspaceId,
}: UseCaseFormEditorOptions) {
  const { t } = useTranslation();
  const [updateCase] = api.useUpdateCaseMutation();
  const { data: caseStatusesResponse } = api.useGetCaseStatusesQuery();
  const { data: subCaseTypeTreeResponse } = api.useGetSubCaseTypeTreeQuery();
  const { data: caseCategoriesResponse } = api.useGetCaseCategoriesQuery();
  const { data: caseTypesResponse } = api.useGetCaseTypesQuery();
  const { data: casePrioritiesResponse } = api.useGetCasePrioritiesQuery();
  const { data: caseOriginationsResponse } = api.useGetCaseOriginationsQuery();
  const { data: employeesResponse } = api.useGetEmployeesByWorkspaceQuery(
    workspaceId ?? 0,
    { skip: !workspaceId }
  );
  const { data: clientContactsResponse } = api.useGetClientContactsQuery(
    workspaceId ?? 0,
    { skip: !workspaceId }
  );
  const { data: internalUsersResponse } = api.useGetInternalUsersQuery();
  const { data: resolutionTypesResponse } = api.useGetResolutionTypesQuery();
  const [fetchCaseTypesByCategory] = api.useLazyGetCaseTypesByCategoryQuery();
  const [filteredCaseTypeOptions, setFilteredCaseTypeOptions] = useState<
    Array<{ value: number; label: string }>
  >([]);
  const [caseFields, setCaseFields] = useState(createEmptyCaseFormFields);
  const [initialCaseFields, setInitialCaseFields] = useState(
    createEmptyCaseFormFields
  );
  const [isSaving, setIsSaving] = useState(false);
  const [justClosed, setJustClosed] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const syncedCaseSignatureRef = useRef<string | null>(null);

  const statusOptions = useMemo(() => {
    const seen = new Set<string>();

    return (caseStatusesResponse?.statuses ?? []).reduce<
      Array<{ value: string; label: string }>
    >((options, status) => {
      if (seen.has(status.code)) return options;
      seen.add(status.code);
      options.push({ value: status.code, label: status.name });
      return options;
    }, []);
  }, [caseStatusesResponse]);

  const originationOptions = useMemo(
    () => toUniqueNamedOptions(caseOriginationsResponse?.originations ?? []),
    [caseOriginationsResponse]
  );
  const priorityOptions = useMemo(
    () => toUniqueNamedOptions(casePrioritiesResponse?.priorities ?? []),
    [casePrioritiesResponse]
  );
  const caseTypeOptions = useMemo(
    () =>
      (caseTypesResponse?.caseTypes ?? []).map(caseType => ({
        value: caseType.id,
        label: caseType.name,
      })),
    [caseTypesResponse]
  );

  const categories = caseCategoriesResponse?.categories ?? [];

  const employeeOptions = useMemo(
    () =>
      (employeesResponse?.employees ?? []).map(emp => ({
        id: emp.id,
        value: emp.id,
        label: `${emp.firstName} ${emp.lastName} (${emp.employeeIdentifier})`,
        displayName: `${emp.firstName} ${emp.lastName}`,
        phone: emp.phone || emp.mobile || '',
        email: emp.email || emp.workEmail || '',
      })),
    [employeesResponse]
  );

  const clientContactOptions = useMemo(
    () =>
      (clientContactsResponse?.contacts ?? []).map(contact => ({
        value: contact.fkContactIDClientContact,
        label: contact.displayName,
      })),
    [clientContactsResponse]
  );

  const resolutionTypeOptions = useMemo(
    () =>
      buildResolutionTypeOptions(
        resolutionTypesResponse?.resolutionTypes ?? []
      ),
    [resolutionTypesResponse]
  );

  const internalUserOptions = useMemo(() => {
    const options = (internalUsersResponse?.users ?? []).map(user => ({
      id: user.id,
      value: user.id,
      label: `${user.name} (${user.userName})`,
      email: user.email,
    }));

    // Ensure the currently assigned user is always in the list, even if
    // they weren't returned by /case/users (e.g. they have a different role).
    // Skip when assignedTo is 0 or null — that means "not assigned".
    if (
      caseInfo?.assignedTo != null &&
      caseInfo.assignedTo !== 0 &&
      caseInfo.assignedToName &&
      !options.some(o => o.value === caseInfo.assignedTo)
    ) {
      options.unshift({
        id: caseInfo.assignedTo,
        value: caseInfo.assignedTo,
        label: caseInfo.assignedToName,
        email: '',
      });
    }

    return options;
  }, [internalUsersResponse, caseInfo?.assignedTo, caseInfo?.assignedToName]);

  // Fetch filtered case types when category changes (for edit mode)
  useEffect(() => {
    if (!caseFields.categoryCode) {
      setFilteredCaseTypeOptions([]);
      return;
    }
    void fetchCaseTypesByCategory(caseFields.categoryCode)
      .unwrap()
      .then(result =>
        setFilteredCaseTypeOptions(
          (result.caseTypes ?? []).map(ct => ({ value: ct.id, label: ct.name }))
        )
      )
      .catch(() => setFilteredCaseTypeOptions([]));
  }, [caseFields.categoryCode, fetchCaseTypesByCategory]);

  // Use filtered types if available, otherwise fall back to all types
  const resolvedCaseTypeOptions =
    filteredCaseTypeOptions.length > 0
      ? filteredCaseTypeOptions
      : caseTypeOptions;

  useEffect(() => {
    if (!syncWhen) {
      syncedCaseSignatureRef.current = null;
      return;
    }

    if (!caseInfo) {
      const empty = createEmptyCaseFormFields();
      syncedCaseSignatureRef.current = null;
      setCaseFields(empty);
      setInitialCaseFields(empty);
      return;
    }

    const matchedStatus = statusOptions.find(
      option =>
        option.value === caseInfo.status || option.label === caseInfo.status
    );
    const resolvedStatus = matchedStatus?.value ?? caseInfo.status;

    const matchedCategory = (caseCategoriesResponse?.categories ?? []).find(
      cat => cat.name === caseInfo.category || cat.code === caseInfo.category
    );
    const resolvedPriority = resolveOptionValue(
      priorityOptions,
      caseInfo.priority
    );
    const resolvedOrigination = resolveOptionValue(
      originationOptions,
      caseInfo.origination
    );
    const resolvedReportingMethod = resolveOptionValue(
      REPORTING_METHOD_OPTIONS,
      caseInfo.reportingMethod
    );
    const resolvedAssignedTo =
      caseInfo.assignedTo != null && caseInfo.assignedTo !== 0
        ? caseInfo.assignedTo
        : null;

    const syncSignature = [
      caseInfo.id,
      resolvedStatus,
      matchedCategory?.code ?? '',
      caseInfo.caseType?.id ?? '',
      resolvedPriority,
      resolvedOrigination,
      resolvedReportingMethod,
      resolvedAssignedTo ?? '',
      caseInfo.employeeId ?? '',
      caseInfo.reportedByEmployeeId ?? '',
      caseInfo.resolutionType ?? '',
      caseInfo.closeCaseLoop ?? '',
      caseInfo.clientContactId ?? '',
    ].join('|');

    if (syncedCaseSignatureRef.current === syncSignature) return;

    const fieldsData: CaseFormFields = {
      status: resolvedStatus,
      subject: caseInfo.subject || '',
      description: caseInfo.description || '',
      notes: caseInfo.notes || '',
      category: caseInfo.category || '',
      categoryCode: matchedCategory?.code ?? '',
      categoryName: matchedCategory?.name ?? caseInfo.category ?? '',
      categoryPriority: resolvedPriority,
      caseType: caseInfo.caseType?.name ?? '',
      caseTypeId: caseInfo.caseType?.id ?? null,
      subCaseType: caseInfo.subCaseType || '',
      origination: resolvedOrigination,
      closeCaseLoop: caseInfo.closeCaseLoop ?? CloseCaseLoop.PHONE,
      resolution: caseInfo.resolution || '',
      phoneNumber: caseInfo.phoneNumber || '',
      email: caseInfo.email || '',
      reportingMethod: resolvedReportingMethod || DEFAULT_CASE_REPORTING_METHOD,
      employeeId:
        caseInfo.employeeId != null && caseInfo.employeeId !== 0
          ? caseInfo.employeeId
          : null,
      reportedByEmployeeId:
        caseInfo.reportedByEmployeeId != null &&
        caseInfo.reportedByEmployeeId !== 0
          ? caseInfo.reportedByEmployeeId
          : null,
      assignedTo: resolvedAssignedTo,
      resolutionType: caseInfo.resolutionType || '',
      reportedByName: caseInfo.reportedByName || '',
      // The API returns clientContactId as a number; dropdown options carry
      // string ids, so normalize to keep the value selectable.
      clientContactId: caseInfo.clientContactId
        ? String(caseInfo.clientContactId)
        : '',
    };

    syncedCaseSignatureRef.current = syncSignature;
    setCaseFields({ ...fieldsData });
    setInitialCaseFields({ ...fieldsData });
  }, [
    syncWhen,
    caseInfo,
    statusOptions,
    caseCategoriesResponse,
    priorityOptions,
    originationOptions,
  ]);

  const hasFieldChanged = (fieldType: CaseFieldType): boolean =>
    isFieldDirty(fieldType, caseFields, initialCaseFields);

  const hasUnsavedChanges = EDITABLE_FIELD_TYPES.some(fieldType =>
    isFieldDirty(fieldType, caseFields, initialCaseFields)
  );

  const handleDiscardNotes = () => {
    setCaseFields(prev => ({
      ...prev,
      notes: initialCaseFields.notes,
    }));
  };

  const handleFormFieldChange = (
    field: keyof CaseFormFields,
    value: string | number | null
  ) => {
    setCaseFields(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveAll = async () => {
    if (!caseInfo || !hasUnsavedChanges) return;

    if (hasFieldChanged('status') && !contactId) {
      setToast({
        message: t('components.contactCenter.caseStatusUpdateMissingContact'),
        type: 'error',
      });
      return;
    }

    if (
      hasFieldChanged('category') &&
      (!caseFields.categoryCode || !caseFields.categoryName)
    ) {
      setToast({
        message: t('components.contactCenter.caseCategoryRequired'),
        type: 'error',
      });
      return;
    }

    if (hasFieldChanged('notes')) {
      const notesDelta = getCaseNotesAppendDelta(
        initialCaseFields.notes,
        caseFields.notes
      );
      if (notesDelta === null) {
        setToast({
          message: t('components.contactCenter.caseNotesAppendOnly'),
          type: 'error',
        });
        return;
      }
    }

    setIsSaving(true);

    try {
      const payload: UpdateCaseRequest = { caseId: caseInfo.id };

      if (hasFieldChanged('status')) {
        payload.status = caseFields.status;
        if (contactId) payload.contactId = contactId;
      }
      if (hasFieldChanged('subject')) payload.subject = caseFields.subject;
      if (hasFieldChanged('description')) {
        payload.description = caseFields.description;
      }
      if (hasFieldChanged('notes')) {
        payload.notes = getCaseNotesAppendDelta(
          initialCaseFields.notes,
          caseFields.notes
        )!;
      }
      if (hasFieldChanged('category')) {
        payload.categoryCode = caseFields.categoryCode;
      }
      if (hasFieldChanged('categoryPriority')) {
        payload.priority = caseFields.categoryPriority;
      }
      if (hasFieldChanged('caseType') && caseFields.caseTypeId != null) {
        payload.caseTypeId = caseFields.caseTypeId;
      }
      if (hasFieldChanged('subCaseType')) {
        payload.subCaseType = caseFields.subCaseType;
      }
      if (hasFieldChanged('origination')) {
        payload.origination = caseFields.origination;
      }
      if (hasFieldChanged('resolution')) {
        payload.resolution = caseFields.resolution;
      }
      if (hasFieldChanged('phoneNumber')) {
        payload.phoneNumber = caseFields.phoneNumber;
      }
      if (hasFieldChanged('email')) payload.email = caseFields.email;
      if (hasFieldChanged('assignedTo')) {
        payload.assignedTo = caseFields.assignedTo ?? undefined;
      }
      if (hasFieldChanged('employeeId')) {
        payload.employeeId = caseFields.employeeId ?? undefined;
      }
      payload.reportedByEmployeeId = caseFields.reportedByEmployeeId ?? 0;
      if (hasFieldChanged('resolutionType')) {
        payload.resolutionType = caseFields.resolutionType || undefined;
      }
      if (hasFieldChanged('closeCaseLoop')) {
        payload.closeCaseLoop = caseFields.closeCaseLoop as CloseCaseLoop;
      }
      if (hasFieldChanged('reportingMethod')) {
        payload.reportingMethod = caseFields.reportingMethod || undefined;
      }
      if (hasFieldChanged('reportedByName')) {
        payload.callerName = caseFields.reportedByName || undefined;
      }
      if (hasFieldChanged('clientContactId')) {
        payload.clientContactId = caseFields.clientContactId || undefined;
      }

      await updateCase(payload).unwrap();
      setInitialCaseFields({ ...caseFields });

      if (hasFieldChanged('status') && isCaseClosed(caseFields.status)) {
        setJustClosed(true);
      }

      onAfterSave?.(caseInfo.id);

      setToast({
        message: t('components.contactCenter.caseUpdateSuccess'),
        type: 'success',
      });
    } catch {
      setToast({
        message: t('components.contactCenter.caseUpdateFailed'),
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    caseFields,
    initialCaseFields,
    handleFormFieldChange,
    hasFieldChanged,
    hasUnsavedChanges,
    handleDiscardNotes,
    handleSaveAll,
    isSaving,
    statusOptions,
    subCaseTypeTree: subCaseTypeTreeResponse?.subCaseTypeTree ?? {},
    originationOptions,
    priorityOptions,
    caseTypeOptions: resolvedCaseTypeOptions,
    categories,
    employeeOptions,
    internalUserOptions,
    resolutionTypeOptions,
    clientContactOptions,
    toast,
    setToast,
    clearToast: () => setToast(null),
    justClosed,
    clearJustClosed: () => setJustClosed(false),
  };
}
